import {
  AttributeInfo,
  Contact,
  ContentMetaData,
  GlobalContent,
  Platform,
  Property
} from '@blisspointmedia/bpm-types/dist/StreamingNetworks';
import { OverviewFieldType, OverviewSection } from '@Components/EntityOverviewForm';
import { toOptions } from '@Components/ModalEditWidget/modal-edit-utils';
import { toPrettySpend } from '@src/utils/performance-utils';

export type { Contact, GlobalContent, Platform, Property, ContentMetaData, AttributeInfo };

// The merged shape this page actually works with: `streamingv2`'s "old" and "new" list endpoints
// each return a disjoint slice of a network's data (see use-streaming-networks.ts) — this is the
// union of both, joined client-side by shortCode. Neither endpoint alone has everything the page
// needs (attributes only exists on the old response; globalContent/contentMetaDataMap only on the
// new one), and the backend has no single endpoint that returns both today.
export interface StreamingNetworkRecord {
  shortCode: string;
  networkGroup: string;
  name: string;
  color: string;
  pdfText: string;
  parent: string | null;
  buyable: boolean;
  cpm: number | null;
  defaultIoPdfText: string;
  ioMinimum: number | null;
  lineItemMinimum: number | null;
  splitLength: boolean | null;
  platforms: Platform[];
  dsp: string[];
  contacts: Contact[];
  properties: Property[];
  globalContent: GlobalContent[];
  contentMetaDataMap: Record<string, ContentMetaData>;
  attributes?: Record<string, Record<string, AttributeInfo | undefined>>;
}

export const AttributeType = {
  MULTI_SELECT: 'multi_select',
  BOOLEAN: 'boolean',
  NUMBER: 'number',
  TEXT: 'text'
} as const;
export type AttributeTypeType = (typeof AttributeType)[keyof typeof AttributeType];

export const WorkspaceTab = {
  GENERAL: 'general',
  CONTACTS: 'contacts',
  CONTENT: 'content',
  CONTENT_DETAILS: 'content-details',
  PROPERTIES: 'properties',
  ATTRIBUTES: 'attributes'
} as const;
export type WorkspaceTabType = (typeof WorkspaceTab)[keyof typeof WorkspaceTab];

export const shortCodeOf = (network: StreamingNetworkRecord): string => network.shortCode;
export const networkGroupOf = (network: StreamingNetworkRecord): string =>
  network.networkGroup || 'Ungrouped';
export const colorOf = (network: StreamingNetworkRecord): string => network.color;

// ---- selector options ----

// The Content tab's platform-prefix picker is a hardcoded 3-value dropdown — there's no
// backend-driven option list for it.
export const CONTENT_PLATFORM_PREFIX_OPTIONS = toOptions(['Streaming', 'Audio', 'Display']);

// ---- General tab (single-network overview form) ----

// 'Network Group' is the one creatable field whose options are computed from the current network
// list at render time — NetworkGeneralTab.tsx fills in `options` before rendering.
export const OVERVIEW_SECTIONS: OverviewSection[] = [
  {
    title: 'Identity',
    fields: [
      { field: 'name', label: 'Name', type: OverviewFieldType.TEXT },
      { field: 'shortCode', label: 'Short Code', type: OverviewFieldType.TEXT, readOnly: true },
      {
        field: 'networkGroup',
        label: 'Network Group',
        type: OverviewFieldType.SELECT,
        creatable: true,
        options: []
      },
      { field: 'parent', label: 'Parent Network', type: OverviewFieldType.TEXT, readOnly: true }
    ]
  },
  {
    title: 'Buying',
    fields: [
      { field: 'buyable', label: 'Buyable', type: OverviewFieldType.CHECKBOX },
      { field: 'splitLength', label: 'Split Length', type: OverviewFieldType.CHECKBOX },
      { field: 'cpm', label: 'Default CPM', type: OverviewFieldType.TEXT },
      {
        field: 'ioMinimum',
        label: 'IO Minimum',
        type: OverviewFieldType.TEXT,
        format: value => toPrettySpend(Number(value) || 0, 2)
      },
      {
        field: 'lineItemMinimum',
        label: 'Line Item Minimum',
        type: OverviewFieldType.TEXT,
        format: value => toPrettySpend(Number(value) || 0, 2)
      }
    ]
  },
  {
    title: 'Presentation',
    fields: [{ field: 'color', label: 'Color', type: OverviewFieldType.COLOR }]
  }
];

// Rendered separately, after the Platforms/DSP list editors — these are long-form paragraphs, not
// scalar fields, so they don't belong squeezed into the scalar-fields grid above.
export const IO_TEXT_SECTIONS: OverviewSection[] = [
  {
    title: 'IO Text',
    fields: [
      {
        field: 'defaultIoPdfText',
        label: 'Default PDF Text',
        type: OverviewFieldType.TEXTAREA,
        fullWidth: true
      },
      { field: 'pdfText', label: 'Terms', type: OverviewFieldType.TEXTAREA, fullWidth: true }
    ]
  }
];

// ---- soft-delete list editing helper ----
// Platforms/contacts/properties/globalContent are all edited the same way: an array of objects with
// a `deleted` flag, never spliced out until save (except unsaved platform rows, which the Platform
// select drops), so array indices stay stable across edits.

interface IndexedRow<T> {
  row: T;
  originalIndex: number;
}

export const indexedVisibleRows = <T extends { deleted?: boolean }>(rows: T[]): IndexedRow<T>[] =>
  rows.map((row, originalIndex) => ({ row, originalIndex })).filter(({ row }) => !row.deleted);

export const updateAt = <T>(rows: T[], index: number, updated: T): T[] =>
  rows.map((row, i) => (i === index ? updated : row));

// A local editable subset of ContentMetaData — excludes `content`/`platformPrefix`, which the map
// key (not the row) already carries, and which the save call takes as separate arguments.
// All fields are required: the backend UPDATEs every column, so a partial payload would NULL the rest.
export interface ContentMetaDataFields {
  genre: string | null;
  publisherGenre: string | null;
  placementGenre: string | null;
  format: string | null;
  pmp: boolean | null;
  guaranteed: boolean | null;
  fepOlv: string | null;
  enabled: boolean | null;
}
